import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import ESpinner from 'components/others/Spinner';
import ObjectsTable from './ObjectsTable';
import ObjectsToolBar from './ObjectsToolBar';
import { OffClientInfo } from 'modules/client';
import { OffTasks } from 'modules/task';
import TitleCounter from 'components/others/TitleCounter';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { withBaseCurrency, withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import useObjectsList from './model/useObjectsList';
import { NewObjectModal, type NewObjectType } from 'modules/lead';
import OffNewSale, { type NewSaleType } from './OffNewSale';
import { useObject } from './context/ObjectProvider';

const ObjectsList = () => {
    const { t } = useTranslation(['Objects', 'common']);
    const { onlySale, onlyService, isSaleLike } = useObject();
    const [newObject, setNewObject] = useState<NewObjectType | null>(null);
    const [newSale, setNewSale] = useState<NewSaleType | null>(null);
    const {
        objectsList,
        paidUsd,
        paidUah,
        showDebtor,
        clientInfoId,
        setClientInfoId,
        tasksObjectId,
        setTasksObjectId,
        tasksContextName,
        fetchObjects,
    } = useObjectsList();

    return (
        <>
            {!isSaleLike && (
                <NewObjectModal newObject={newObject} setNewObject={setNewObject} />
            )}
            <OffNewSale newSale={newSale} setNewSale={setNewSale} />

            <OffClientInfo clientId={clientInfoId} setClientId={setClientInfoId} />

            <OffTasks
                show={tasksObjectId !== null}
                onHide={() => setTasksObjectId(null)}
                objectId={tasksObjectId}
                contextName={tasksContextName}
            />

            <Row>
                <Col className="col-sticky" sm={12}>
                    <div className="objects-list-header w-100 pt-1">
                        <div className="objects-list-header__meta">
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">
                                {onlyService
                                    ? t('titleServiceSalesList')
                                    : onlySale
                                        ? t('titleSalesList')
                                        : t('titleList')}
                            </h4>
                            <div className="objects-list-header__counters d-flex flex-wrap align-items-center">
                                <TitleCounter />
                                {showDebtor ? (
                                    <TitleCounter
                                        color={paidUsd > 0 || paidUah > 0 ? 'success' : 'secondary'}
                                        title={t('fields.payments')}
                                        count={
                                            [
                                                paidUsd ? withBaseCurrency(formatPrice(paidUsd)) : null,
                                                paidUah ? withLocalCurrency(formatPrice(paidUah)) : null,
                                            ]
                                                .filter(Boolean)
                                                .join('  ') || undefined
                                        }
                                        noShowCount={!paidUsd && !paidUah}
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className="objects-list-header__toolbar">
                            <ObjectsToolBar
                                setNewObject={setNewObject}
                                setNewSale={setNewSale}
                            />
                        </div>
                    </div>
                </Col>

                <Col sm={12}>
                    {objectsList ? (
                        <ObjectsTable
                            setClientInfoId={setClientInfoId}
                            setTasksObjectId={setTasksObjectId}
                            objectsList={objectsList}
                            fetchObjects={fetchObjects}
                        />
                    ) : (
                        <ESpinner />
                    )}
                </Col>
            </Row>
        </>
    );
};

export default ObjectsList;
